import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { formatUSPhone } from "../../../utils";
import { useCompanyNumberQuery } from "../../virtual-receptionist/widget/components/NumbersConfiguration/hooks/useCompanyNumberQuery";
import { useMessageGroupAdminMutations } from "../hooks/useMessageGroupAdminMutations";
import { GROUP_MMS_MAX_EXTERNAL_PARTICIPANTS, GroupNumberValidation, MessageGroupSummary } from "../messageGroups.types";
import { MemberPicker, PickableMember, buildContactMember, memberKeyOf, toE164 } from "./MemberPicker";

interface CreateSmsGroupModalProps {
  open: boolean;
  companyId?: number | string | null;
  onClose: () => void;
  onCreated?: (group: MessageGroupSummary) => void;
}

const MIN_PARTICIPANTS = 2;

export const CreateSmsGroupModal = ({ open, companyId, onClose, onCreated }: CreateSmsGroupModalProps) => {
  const [step, setStep] = useState(0);
  const [members, setMembers] = useState<PickableMember[]>([]);
  const [manualNumber, setManualNumber] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const [validations, setValidations] = useState<Record<string, GroupNumberValidation>>({});
  const [name, setName] = useState("");
  const [smsFromNumber, setSmsFromNumber] = useState("");

  const { createGroup, validateNumbers } = useMessageGroupAdminMutations(undefined, companyId);
  const activeCompanyId = useSelector((state: any) => state.userState.activeCompanyId);
  const { data: companyNumbers = [] } = useCompanyNumberQuery();

  useEffect(() => {
    if (smsFromNumber || !companyNumbers.length) return;
    setSmsFromNumber(companyNumbers[0]?.phoneNumber ?? "");
  }, [companyNumbers, smsFromNumber]);

  const pendingNumbers = useMemo(
    () => members.map((member) => member.phoneNumber as string).filter((phoneNumber) => !validations[phoneNumber]),
    [members, validations],
  );

  const isOwnCompanyNumber = (validation?: GroupNumberValidation | null) =>
    Boolean(
      validation?.isFlownectNumber &&
        activeCompanyId != null &&
        Number(validation.flownectCompanyId) === Number(activeCompanyId),
    );

  const rejected = useMemo(
    () =>
      members.filter((member) => {
        const validation = validations[member.phoneNumber as string];
        return validation?.canJoinGroup === false || isOwnCompanyNumber(validation);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, validations, activeCompanyId],
  );

  const isOverCapacity = members.length > GROUP_MMS_MAX_EXTERNAL_PARTICIPANTS;

  const canContinue = members.length >= MIN_PARTICIPANTS && !isOverCapacity && !rejected.length && !pendingNumbers.length;
  const canCreate = canContinue && name.trim().length > 0 && Boolean(smsFromNumber) && !createGroup.isPending;
  const errorMessage = (createGroup.error as any)?.response?.data?.message ?? (createGroup.error as Error)?.message;

  const unverified = (phoneNumber: string, reason: string): GroupNumberValidation => ({
    input: phoneNumber,
    phoneNumber,
    isValid: false,
    canJoinGroup: false,
    isFlownectNumber: false,
    flownectCompanyId: null,
    lineType: null,
    carrierName: null,
    contactName: null,
    warning: null,
    reason,
  });

  const requestedNumbersRef = useRef<Set<string>>(new Set());
  const numbersToValidate = pendingNumbers.filter((phoneNumber) => !requestedNumbersRef.current.has(phoneNumber));
  const numbersToValidateKey = numbersToValidate.join(",");

  const { mutateAsync: validateNumbersAsync } = validateNumbers;

  useEffect(() => {
    if (!open || !numbersToValidateKey) return;

    const numbers = numbersToValidateKey.split(",");
    numbers.forEach((phoneNumber) => requestedNumbersRef.current.add(phoneNumber));

    validateNumbersAsync(numbers)
      .then((results) => {
        setValidations((current) => {
          const next = { ...current };

          numbers.forEach((phoneNumber) => {
            const match = results.find((result) => result.phoneNumber === phoneNumber || result.input === phoneNumber);
            next[phoneNumber] = match ?? unverified(phoneNumber, "Twilio did not return a result for this number");
          });

          return next;
        });
      })
      .catch(() => {
        numbers.forEach((phoneNumber) => requestedNumbersRef.current.delete(phoneNumber));

        setValidations((current) => {
          const next = { ...current };
          numbers.forEach((phoneNumber) => {
            next[phoneNumber] = unverified(phoneNumber, "Could not verify this number, try again");
          });
          return next;
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, numbersToValidateKey]);

  const reset = () => {
    setStep(0);
    setMembers([]);
    setManualNumber("");
    setManualError(null);
    setValidations({});
    requestedNumbersRef.current = new Set();
    setName("");
    createGroup.reset();
    validateNumbers.reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAddManualNumber = () => {
    const phoneNumber = toE164(manualNumber);

    if (!phoneNumber) {
      setManualError("Not a valid US or Canada number: 10 digits and the area code cannot start with 0 or 1");
      return;
    }

    if (members.some((member) => member.uid === phoneNumber)) {
      setManualError("That number is already on the list");
      return;
    }

    setManualError(null);
    setManualNumber("");
    setMembers((current) => [...current, buildContactMember(phoneNumber)]);
  };

  useEffect(() => {
    setMembers((current) => {
      let changed = false;

      const next = current.map((member) => {
        const resolved = validations[member.phoneNumber as string]?.contactName;

        if (!resolved || member.displayName === resolved) return member;

        changed = true;
        return { ...member, displayName: resolved, name: resolved };
      });

      return changed ? next : current;
    });
  }, [validations]);

  const handleRemoveMember = (member: PickableMember) => {
    setMembers((current) => current.filter((item) => memberKeyOf(item) !== memberKeyOf(member)));
  };

  const handleCreate = async () => {
    const created = await createGroup.mutateAsync({
      name: name.trim(),
      channel: "sms",
      smsFromNumber,
      members: members.map((member) => ({
        type: validations[member.phoneNumber as string]?.isFlownectNumber ? ("companyNumber" as const) : member.type,
        uid: member.uid,
        phoneNumber: member.phoneNumber,
        displayName: member.displayName,
      })),
    });

    onCreated?.(created);
    handleClose();
  };

  const renderValidation = (member: PickableMember) => {
    const validation = validations[member.phoneNumber as string];

    if (!validation) {
      return <CircularProgress size={14} />;
    }

    if (!validation.canJoinGroup) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ErrorRoundedIcon sx={{ fontSize: 14, color: "error.main" }} />
          <Typography sx={{ fontSize: 11, color: "error.main" }}>{validation.reason ?? "Cannot join a group text"}</Typography>
        </Box>
      );
    }

    if (isOwnCompanyNumber(validation)) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ErrorRoundedIcon sx={{ fontSize: 14, color: "error.main" }} />
          <Typography sx={{ fontSize: 11, color: "error.main" }}>This is your own number, it is already in the group</Typography>
        </Box>
      );
    }

    if (validation.isFlownectNumber) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 14, color: "info.main" }} />
          <Typography sx={{ fontSize: 11, color: "info.main" }}>
            {validation.flownectCompanyId ? "Flownect number · joins as a member" : "Flownect number · another environment"}
          </Typography>
        </Box>
      );
    }

    if (validation.warning) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 14, color: "warning.main" }} />
          <Typography sx={{ fontSize: 11, color: "warning.main" }}>
            {validation.carrierName ? `${validation.carrierName} · ` : ""}
            {validation.warning}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 14, color: "success.main" }} />
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{validation.contactName ?? "Phone number"}</Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth sx={{ zIndex: 2147483000 }}>
      <DialogTitle sx={{ pb: 0.5, fontSize: 17, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <SmsRoundedIcon sx={{ fontSize: 18, color: "warning.main" }} />
        New group text
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
        <Stepper activeStep={step} alternativeLabel>
          <Step>
            <StepLabel>Numbers</StepLabel>
          </Step>
          <Step>
            <StepLabel>Details</StepLabel>
          </Step>
        </Stepper>

        {step === 0 ? (
          <>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                value={manualNumber}
                onChange={(event) => setManualNumber(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAddManualNumber();
                  }
                }}
                placeholder="(305) 555-1234"
                label="Add a number"
                size="small"
                fullWidth
                error={Boolean(manualError)}
                helperText={manualError}
              />
              <Button onClick={handleAddManualNumber} size="small" variant="outlined" sx={{ alignSelf: "flex-start", height: 40 }}>
                Add
              </Button>
            </Box>

            {members.length > 0 && (
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 180, overflowY: "auto" }}>
                {members.map((member) => (
                  <Box
                    key={memberKeyOf(member)}
                    sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                        {member.name}
                      </Typography>
                      {renderValidation(member)}
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveMember(member)} aria-label={`Remove ${member.name}`}>
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Or pick from your contacts</Typography>

            <MemberPicker selected={members} onChange={setMembers} mode="sms" companyId={companyId} emptyLabel="No SMS contacts yet" />

            <Typography
              sx={{ fontSize: 11.5, color: isOverCapacity || rejected.length ? "error.main" : "text.secondary" }}
            >
              {isOverCapacity
                ? `A group text supports up to ${GROUP_MMS_MAX_EXTERNAL_PARTICIPANTS} numbers`
                : rejected.length
                  ? "Remove the numbers that cannot join a group text"
                  : `${members.length} of ${GROUP_MMS_MAX_EXTERNAL_PARTICIPANTS} numbers · everyone in the group sees each other`}
            </Typography>
          </>
        ) : (
          <>
            <TextField
              label="Group name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              fullWidth
              autoFocus
              inputProps={{ maxLength: 120 }}
            />

            <TextField
              select
              label="Send from"
              value={smsFromNumber}
              onChange={(event) => setSmsFromNumber(event.target.value)}
              size="small"
              fullWidth
              helperText={
                companyNumbers.length
                  ? "This company number joins the group text and is what everyone replies to"
                  : "No company number available to send SMS"
              }
            >
              {companyNumbers.map((number) => (
                <MenuItem key={number.uid} value={number.phoneNumber}>
                  {number.name ? `${number.name} · ` : ""}
                  {formatUSPhone(number.phoneNumber) || number.phoneNumber}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {members.map((member) => (
                <Chip key={memberKeyOf(member)} label={member.name} size="small" />
              ))}
            </Box>
          </>
        )}

        {errorMessage && <Typography sx={{ fontSize: 12, color: "error.main" }}>{errorMessage}</Typography>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={step === 0 ? handleClose : () => setStep(0)} size="small" color="inherit">
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step === 0 ? (
          <Button onClick={() => setStep(1)} size="small" variant="contained" disabled={!canContinue}>
            Next
          </Button>
        ) : (
          <Button onClick={handleCreate} size="small" variant="contained" disabled={!canCreate}>
            {createGroup.isPending ? "Creating..." : "Create group text"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
